'use client';

import Image from 'next/image';
import { useEffect, useState, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { getScrollForMoreThreshold } from '@/lib/scrollForMoreThresholds';

export default function ScrollForMoreOverlay() {
  const pathname = usePathname();
  const isHomepage = pathname === '/';
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);
  const [dvh, setDvh] = useState(0);
  const [shouldShow, setShouldShow] = useState(true);
  const wasHiddenByThresholdRef = useRef(false);
  const currentPathnameRef = useRef(pathname);

  // Reset threshold tracking when pathname changes
  useEffect(() => {
    if (currentPathnameRef.current !== pathname) {
      wasHiddenByThresholdRef.current = false;
      currentPathnameRef.current = pathname;
    }
  }, [pathname]);

  // Check dvh and mobile landscape
  useEffect(() => {
    const updateMeasurements = () => {
      // Get dvh (visual viewport height)
      const visualVpHeight = window.visualViewport?.height ?? window.innerHeight;
      setDvh(visualVpHeight);
      
      // Get current threshold (fluid if configured)
      const currentThreshold = getScrollForMoreThreshold(pathname, window.innerWidth);
      
      // Check if dvh is below threshold (mobile only)
      if (currentThreshold !== null && window.innerWidth < 1024) {
        // If we've already been hidden by threshold on this page, keep it hidden
        if (wasHiddenByThresholdRef.current) {
          setShouldShow(false);
        } else {
          // First time checking - hide if below threshold and mark as hidden
          const isAboveThreshold = visualVpHeight >= currentThreshold;
          if (!isAboveThreshold) {
            wasHiddenByThresholdRef.current = true;
            setShouldShow(false);
          } else {
            setShouldShow(true);
          }
        }
      } else {
        // No threshold or desktop - show normally (unless previously hidden by threshold on this page)
        if (wasHiddenByThresholdRef.current) {
          setShouldShow(false);
        } else {
          setShouldShow(true);
        }
      }
    };

    const checkMobileLandscape = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const width = window.innerWidth;
      const height = window.innerHeight;
      
      // Detect mobile phone (not tablet) using user agent
      const isMobilePhone = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) &&
        !/iPad|Tablet|PlayBook|Silk/i.test(userAgent);
      
      // Landscape: width > height
      const isLandscape = width > height;
      
      setIsMobileLandscape(isMobilePhone && isLandscape);
      
      // Also update dvh measurements
      updateMeasurements();
    };

    updateMeasurements();
    checkMobileLandscape();
    
    window.addEventListener('resize', checkMobileLandscape);
    window.addEventListener('orientationchange', checkMobileLandscape);
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateMeasurements);
    }
    
    return () => {
      window.removeEventListener('resize', checkMobileLandscape);
      window.removeEventListener('orientationchange', checkMobileLandscape);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateMeasurements);
      }
    };
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (hasScrolled) return;
      const scrollY = window.scrollY;
      if (scrollY > 50) {
        setHasScrolled(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled]);

  const handleClick = () => {
    const viewportHeight = window.innerHeight;
    window.scrollTo({
      top: viewportHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="scroll-for-more-mobile pointer-events-none fixed inset-0 z-30 flex items-end justify-center lg:hidden"
      style={{ height: '100dvh' }}
      aria-hidden="true"
    >
      <div
        className={`flex flex-col items-center gap-2 cursor-pointer mb-6 transition-opacity duration-300 ease-out py-2 px-4 rounded-lg ${hasScrolled || isMobileLandscape || !shouldShow ? 'opacity-0 pointer-events-none' : 'opacity-100 pointer-events-auto'}`}
        onClick={handleClick}
      >
        <p className="text-center !text-[0.75rem] uppercase">SCROLL FOR MORE</p>
        <Image
          src="/images/underline.svg"
          alt=""
          width={160}
          height={12}
          className="mx-auto"
          priority
        />
      </div>
    </div>
  );
}
